import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, Illustration, Link, Typography, useToast } from "@aviary";
import { useBreakpoints } from "@shared/hooks";
import { WellevateAuthWrapper } from "@unauthenticated/shared/components/WellevateAuthWrapper";
import { l } from "@unauthenticated/wellevateAuthorize/patients/locales/i18n";

import { useWellevateNewAccountEmail } from "./WellevateNewAccountEmail.mutation";

import * as styles from "./AuthorizeFailExpiredToken.styles";

interface Props {
  patientEmail: string;
}

const AuthorizeFailExpiredToken = ({ patientEmail }: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { makeToast } = useToast();
  const [hasResentEmail, setHasResentEmail] = useState<boolean>(false);

  const isMobile = tablet.lessThan;

  const [wellevateNewAccountEmail] = useWellevateNewAccountEmail({
    onCompleted: () => handleOnCompleted(),
    onError: ({ message }) => makeToast("error", message),
  });

  const handleOnCompleted = () => {
    setHasResentEmail(true);
    makeToast("success", t(l.common.resentEmailSuccess));
  };

  const resendEmail = () => {
    wellevateNewAccountEmail({
      variables: {
        input: {
          email: patientEmail,
        },
      },
    });
  };

  const renderCTA = () =>
    hasResentEmail ? (
      <Typography>
        <Trans i18nKey={l.common.resentEmailMessage}>
          Still haven't received an email? Go to this
          <Link href="https://support.fullscript.com/hc/en-us">Contact us</Link>
          for help.
        </Trans>
      </Typography>
    ) : (
      <Button isFullWidth={isMobile} onClick={resendEmail}>
        {t(l.common.resendEmail)}
      </Button>
    );

  return (
    <WellevateAuthWrapper>
      <div css={styles.container}>
        <Typography type="h1" sizeOverride="h2" isMarginless>
          {t(l.common.expiredTokenMessage.title)}
        </Typography>
        <Illustration
          illustration="envelope"
          heightAndWidth={isMobile ? 8 : 15}
          css={styles.illustration}
        />
        <Typography type="h2" sizeOverride={isMobile ? "h4" : "h3"} isMarginless>
          {t(l.common.expiredTokenMessage.subTitle, { patientEmail })}
        </Typography>

        <Typography css={styles.content} isMarginless>
          <Trans i18nKey={l.common.expiredTokenMessage.content}>
            Click the button in the email to finish setting up your account.
            <br />
            <br />
            Didn't get an email? It may be in your SPAM folder. If not, click below to resend.
          </Trans>
        </Typography>
        {renderCTA()}
      </div>
    </WellevateAuthWrapper>
  );
};

export { AuthorizeFailExpiredToken };
